'use client'

import { useState, useEffect } from 'react'

export default function FloatingBugReporter() {
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [currentUrl, setCurrentUrl] = useState('')

  // Grab the exact URL the user is on when they open the modal
  useEffect(() => {
    setCurrentUrl(window.location.href)
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    setStatus('loading')

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, url: currentUrl })
      })

      if (!res.ok) throw new Error('Failed to submit')

      setStatus('success')
      setTimeout(() => {
        setIsOpen(false)
        setMessage('')
        setStatus('idle')
      }, 3000)
    } catch (error) {
      setStatus('error')
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      
      {/* The Expanding Modal */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 sm:w-[380px] bg-slate-950/95 backdrop-blur-2xl border border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-[2rem] p-6 animate-in slide-in-from-bottom-5 duration-200">
          
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xs font-mono font-bold text-slate-300 uppercase tracking-widest flex items-center gap-2">
              <span className="text-amber-400">🐛</span> telemetry / bug_report
            </h3>
            <button 
              onClick={() => setIsOpen(false)} 
              className="text-slate-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-900"
            >
              ✕
            </button>
          </div>

          {status === 'success' ? (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 text-center">
              <div className="text-2xl mb-2">✅</div>
              <div className="text-emerald-400 font-bold text-sm">Payload Received</div>
              <div className="text-emerald-500/80 text-xs font-mono mt-1">Thanks for strengthening the alpha build.</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
                  Describe anomaly:
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="I clicked the post button and encountered..."
                  className="w-full h-32 bg-slate-900/80 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all resize-none shadow-inner"
                  required
                />
              </div>

              {/* CLI-style Context Bar */}
              <div className="text-[10px] font-mono text-slate-400 bg-slate-900/50 px-3 py-2 rounded-xl border border-slate-800/80 truncate flex items-center gap-2">
                <span className="text-amber-500 shrink-0">path:</span> 
                <span className="truncate opacity-80">{currentUrl.replace(/^https?:\/\//, '')}</span>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] font-mono">
                  {status === 'error' && <span className="text-rose-400">Transmission failed. Retry?</span>}
                </span>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-2.5 px-5 rounded-xl text-xs transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? 'Transmitting...' : 'Transmit Report'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* The Floating Action Button with a Pulsing Glow */}
      <div className="relative group">
        {/* Pulsing radar ring */}
        {!isOpen && (
          <div className="absolute inset-0 rounded-full bg-amber-500/30 blur-md animate-ping pointer-events-none" />
        )}

        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative w-12 h-12 rounded-2xl shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-105 cursor-pointer ${
            isOpen 
              ? 'bg-slate-900 text-slate-400 border border-slate-800 shadow-none' 
              : 'bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 text-slate-950 shadow-amber-500/30 border border-yellow-300/30 font-bold'
          }`}
          aria-label="Report Bug"
        >
          {isOpen ? (
            <span className="text-sm font-bold">✕</span>
          ) : (
            <span className="text-xl filter drop-shadow-sm">🐛</span>
          )}
        </button>
      </div>

    </div>
  )
}